import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import LaunchIcon from '@mui/icons-material/Launch';
import { VulnerabilityService } from 'services/VulnerabilityService';
import { IncidentService } from 'services/IncidentService';
import { IIncident } from 'modules/incidents/incidents-log/components/IncidentsCard';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface ILinkedIncidentProps {
  linkedIncidents: IIncident[];
  vulnerabilityId: string;
  reloadDetail: () => void;
}

const LinkedIncidents: React.FC<ILinkedIncidentProps> = (props) => {
  const { linkedIncidents, vulnerabilityId, reloadDetail } = props;
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IIncident[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [isLinking, setIsLinking] = useState('');
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  // API Calls
  const loadIncidents = (): void => {
    setIsLoadingRecords(true);
    IncidentService.getAll('')
      .then((response) => {
        const { data } = response.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(data.incidents.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const linkRecord = (payload: any, callback: any): void => {
    VulnerabilityService.linkIncident(vulnerabilityId, payload)
      .then((response) => {
        callback(response);
      })
      .catch(() => {
        callback(false);
      });
  };

  // UI Actions
  const actionOnLink = (status: string, id: string): void => {
    setIsLinking(status);
    linkRecord(
      {
        incidentId: id,
        action: status,
      },
      (response: any) => {
        setIsLinking('');
        setSelectedRecordId('');
        if (typeof response === 'object') {
          reloadDetail();
        }
      }
    );
  };

  // Hooks
  useEffect(() => {
    loadIncidents();
  }, []);

  // UI Elements
  return (
    <Box className="app-plain-card">
      <Box className="title">
        <Stack spacing={2} direction="row" alignItems="center">
          <img height={20} src="/images/icons/warning.svg" />
          <Typography variant="h4">Related Incidents</Typography>
        </Stack>
        <Typography className="text-gray">
          Incidents related to this vulnerability
        </Typography>
      </Box>

      <Box className="content">
        {isLoadingRecords && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoadingRecords && (
          <>
            {orgACLConfig.vulnerabilities?.includes(
              'vulnerabilities.aiVulnerabilities.incidents.link'
            ) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.5, sm: 4 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: '100%' }}
              >
                <AppSelectWithKeys
                  onChange={(e: any) => {
                    setSelectedRecordId(e.target.value);
                  }}
                  items={records.filter(
                    (r) => !linkedIncidents.some((ls) => ls._id === r._id)
                  )}
                  titleKey="title"
                  valueKey="_id"
                />
                <AppPrimaryButtonOutlined
                  isLoading={isLinking === 'link'}
                  disabled={!selectedRecordId}
                  title="Link"
                  onClick={() => {
                    actionOnLink('link', selectedRecordId);
                  }}
                />
              </Stack>
            )}
            {linkedIncidents.length > 0 && (
              <List className="app-box-border mt-4 static-box">
                {linkedIncidents.map((incident, index) => (
                  <>
                    <ListItem>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={{ xs: 1.5, sm: 0 }}
                        sx={{ width: '100%' }}
                      >
                        <Stack>
                          <Link to={`/incidents/${incident._id}`}>
                            <Stack
                              className="text-primary mb-2"
                              alignItems="center"
                              direction="row"
                              spacing={2}
                            >
                              <LaunchIcon />
                              <Typography className="f18 fw-6">
                                {incident.title}
                              </Typography>
                            </Stack>
                          </Link>
                          <Stack direction="row" spacing={2}>
                            <Chip size="small" label={`${incident.severity}`} />
                            <Chip size="small" label={incident.status} />
                          </Stack>
                        </Stack>
                        {orgACLConfig.vulnerabilities?.includes(
                          'vulnerabilities.aiVulnerabilities.incidents.unlink'
                        ) && (
                          <AppPrimaryButton
                            isLoading={selectedRecordId === incident._id}
                            onClick={() => {
                              setSelectedRecordId(incident._id);
                              actionOnLink('unlink', incident._id);
                            }}
                            className="btn-danger btn-extra-small"
                            title="Unlink"
                          />
                        )}
                      </Stack>
                    </ListItem>
                    {index < linkedIncidents.length - 1 && <Divider />}
                  </>
                ))}
              </List>
            )}
            {linkedIncidents.length === 0 && (
              <Box className="empty-content">
                <Typography className="f18">
                  No incidents linked to this vulnerability
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LinkedIncidents;
